import type { AutomergeUrl } from "@automerge/automerge-repo";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { TaskActivity } from "apprentices/task-activity";
import {
  ArrowUpRight,
  Box,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  GitBranch,
  Info,
  Layers3,
  Loader2,
  Menu,
  MessageSquare,
  MessageSquarePlus,
  Search,
} from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { invariant } from "ts-invariant";

import { NavUser } from "@/components/nav-user";
import {
  ProjectCommandMenu,
  type CommandMenuGroup,
} from "@/components/ProjectCommandMenu";
import { ProjectEmailAddress } from "@/components/ProjectEmailAddress";
import { Button } from "@/components/ui/button";
import { CommandDialog } from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ProjectJigs } from "@/jigs/ProjectJigs";
import { useTaskActivity } from "@/lib/task-activity";
import { cn } from "@/lib/utils";
import { workshopTaskState } from "@/lib/workshop-task-state";
import {
  ProjectChatsContext,
  useProjectChats,
} from "@/panes/use-project-chats";

export interface ShellTask {
  id: string;
  title: string;
  status: "open" | "applied" | "completed" | "archived";
  migration: unknown;
  chatId?: string | null;
  activity?: TaskActivity;
}

const ProjectCommandContext = createContext<
  ((groups: CommandMenuGroup[]) => void) | null
>(null);
const ProjectTasksContext = createContext<
  ((tasks: ShellTask[] | null) => void) | null
>(null);

export function useProjectShellCommands(groups: CommandMenuGroup[]) {
  const register = useContext(ProjectCommandContext);
  useEffect(() => {
    register?.(groups);
    return () => register?.([]);
  }, [groups, register]);
}

const ProjectJigNameContext = createContext<
  ((name: string | null) => void) | null
>(null);

export function useProjectShellJigName(name: string) {
  const register = useContext(ProjectJigNameContext);
  useEffect(() => {
    register?.(name);
    return () => register?.(null);
  }, [name, register]);
}

export function useProjectShellTasks(tasks: ShellTask[]) {
  const register = useContext(ProjectTasksContext);
  useEffect(() => {
    register?.(tasks);
    return () => register?.(null);
  }, [tasks, register]);
}

export function ProjectShell({
  projectId,
  indexUrl,
  projectName,
  projectEmail,
  section,
  taskId,
  taskTitle,
  tasks,
  children,
}: {
  projectId: string;
  indexUrl: AutomergeUrl;
  projectName: string;
  /** The project's address, when it has one (`ProjectEmailAddress`). */
  projectEmail: string | null;
  section: "workshop" | "explorer" | "jig";
  taskId?: string;
  taskTitle?: string;
  tasks?: ShellTask[];
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const { scope, appId } = useParams({ strict: false });
  const [jigName, setJigName] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [completedOpen, setCompletedOpen] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [screenCommands, setScreenCommands] = useState<CommandMenuGroup[]>([]);
  const [screenTasks, setScreenTasks] = useState<ShellTask[] | null>(null);
  const registerCommands = useCallback((groups: CommandMenuGroup[]) => {
    setScreenCommands(groups);
  }, []);
  const [loadedTasks, setLoadedTasks] = useState<ShellTask[]>([]);
  const location = useLocation();
  // The open conversation belongs to the project, not to one view: it stays
  // open, beside whatever the member opens, until they close it.
  const chats = useProjectChats(projectId);
  const mainChats = chats.list
    .filter((chat) => chat.task_id === null)
    .slice(0, 8);
  // A task's page already holds its conversation.
  const canStartAgent = !(section === "workshop" && taskId);
  const openInExplorer = (branch: string) => {
    if (section === "explorer" && branch === (taskId ?? "main")) return;
    void navigate({
      to: "/p/$projectId/$branch/explorer",
      params: { projectId, branch },
      search: {},
    });
  };
  const startAgent = () => {
    chats.startDraft();
    openInExplorer(taskId ?? "main");
  };
  useEffect(() => {
    if (tasks || screenTasks) return;
    let live = true;
    void fetch(`/api/tasks?projectId=${encodeURIComponent(projectId)}`)
      .then((response) => (response.ok ? response.json() : []))
      .then((rows: ShellTask[]) => {
        if (live) setLoadedTasks(rows);
      })
      .catch(() => {
        if (live) setLoadedTasks([]);
      });
    return () => {
      live = false;
    };
  }, [projectId, tasks, screenTasks, location.pathname]);
  const visibleTasks = (tasks ?? screenTasks ?? loadedTasks).filter(
    (task) => task.status !== "archived",
  );
  const shownTaskTitle =
    taskTitle ?? visibleTasks.find((task) => task.id === taskId)?.title;
  const completedTasks = visibleTasks.filter(
    (task) => workshopTaskState(task, task.activity).section === "completed",
  );
  const activeTasks = visibleTasks.filter(
    (task) => !completedTasks.includes(task),
  );
  const selectedCompletedTaskId = completedTasks.find(
    (task) => task.id === taskId,
  )?.id;
  useEffect(() => {
    if (selectedCompletedTaskId) setCompletedOpen(true);
  }, [selectedCompletedTaskId]);
  const commandGroups = useMemo<CommandMenuGroup[]>(
    () => [
      {
        id: "navigation",
        label: "Go to",
        items: [
          {
            id: "workshop",
            label: "Workshop",
            run: () =>
              void navigate({
                to: "/p/$projectId/workshop",
                params: { projectId },
                search: {},
              }),
          },
          {
            id: "explorer",
            label: "Explorer",
            run: () =>
              void navigate({
                to: "/p/$projectId/$branch/explorer",
                params: { projectId, branch: "main" },
                search: {},
              }),
          },
          ...visibleTasks.map((task) => ({
            id: `task:${task.id}`,
            label: task.title,
            description: "Agent task",
            keywords: ["task", "agent"],
            run: () =>
              void navigate({
                to: "/p/$projectId/$branch/workbench/$view",
                params: { projectId, branch: task.id, view: "changes" },
                search: {},
              }),
          })),
        ],
      },
      ...screenCommands,
    ],
    [navigate, projectId, screenCommands, visibleTasks],
  );

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div className="flex h-full min-h-0 w-full overflow-hidden">
      {sidebarOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
          aria-label="Close navigation"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-[226px] shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground md:static",
          sidebarOpen ? "flex" : "hidden md:flex",
        )}
      >
        <Link
          to="/"
          className="flex h-[84px] shrink-0 items-center gap-2 px-6 font-semibold tracking-tight"
        >
          <Box className="size-6 stroke-[1.7]" />
          <span className="bear-brand text-[29px] leading-none">Bear</span>
        </Link>
        <div className="flex min-w-0 items-center gap-2 px-4 py-3">
          <span className="grid size-8 shrink-0 place-items-center rounded-md border bg-secondary font-serif text-lg text-primary">
            {projectName.trim().charAt(0).toUpperCase() || "B"}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-semibold">{projectName}</div>
            {projectEmail ? (
              <ProjectEmailAddress address={projectEmail} />
            ) : null}
          </div>
        </div>
        <nav className="min-h-0 flex-1 overflow-y-auto px-4 text-xs">
          <div className="flex flex-col gap-1">
            <Link
              to="/p/$projectId/workshop"
              aria-label="Workshop"
              params={{ projectId }}
              search={{}}
              onClick={() => setSidebarOpen(false)}
              className={cn(
                "flex items-center gap-2 rounded-md px-2.5 py-2 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground",
                section === "workshop" &&
                  "bg-sidebar-accent font-semibold text-sidebar-foreground",
              )}
            >
              <FolderOpen className="size-4" /> Workshop
              {activeTasks.length > 0 ? (
                <span className="ml-auto text-[10px] text-muted-foreground">
                  {activeTasks.length}
                </span>
              ) : null}
            </Link>
            <Link
              to="/p/$projectId/$branch/explorer"
              params={{ projectId, branch: "main" }}
              search={{}}
              onClick={() => setSidebarOpen(false)}
              className={cn(
                "flex items-center gap-2 rounded-md px-2.5 py-2 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground",
                section === "explorer" &&
                  "bg-sidebar-accent font-semibold text-sidebar-foreground",
              )}
            >
              <Layers3 className="size-4" /> Explorer
            </Link>
          </div>
          <ProjectJigs
            key={projectId}
            projectId={projectId}
            indexUrl={indexUrl}
            onSelect={() => setSidebarOpen(false)}
          />
          {mainChats.length > 0 ? (
            <div className="mt-5">
              <div className="mb-2 px-2.5 text-[10px] font-medium uppercase tracking-[0.13em] text-muted-foreground">
                Conversations
              </div>
              <div className="flex flex-col gap-0.5">
                {mainChats.map((chat) => (
                  <button
                    key={chat.id}
                    type="button"
                    onClick={() => {
                      setSidebarOpen(false);
                      chats.openThread(chat.id);
                      openInExplorer("main");
                    }}
                    className={cn(
                      "flex items-start gap-2 rounded-md px-2.5 py-2 text-left leading-5 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground",
                      chats.open?.kind === "thread" &&
                        chats.open.id === chat.id &&
                        "bg-sidebar-accent text-sidebar-foreground",
                    )}
                  >
                    <MessageSquare className="mt-[3px] size-3.5 shrink-0" />
                    <span className="min-w-0 line-clamp-2 break-words">
                      {chat.title ?? "New conversation"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {activeTasks.length > 0 ? (
            <div className="mt-5">
              <div className="mb-2 px-2.5 text-[10px] font-medium uppercase tracking-[0.13em] text-muted-foreground">
                Agent tasks
              </div>
              <div className="flex flex-col gap-0.5">
                {activeTasks.map((task) => (
                  <SidebarTask
                    key={task.id}
                    projectId={projectId}
                    task={task}
                    active={section === "workshop" && taskId === task.id}
                    onSelect={() => setSidebarOpen(false)}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {completedTasks.length > 0 ? (
            <div className="mt-5">
              <button
                type="button"
                aria-expanded={completedOpen}
                onClick={() => setCompletedOpen((open) => !open)}
                className="flex w-full items-center gap-1.5 rounded-md px-2.5 py-2 text-left text-[10px] font-medium uppercase tracking-[0.1em] text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
              >
                <ChevronRight
                  className={cn(
                    "size-3 transition-transform",
                    completedOpen && "rotate-90",
                  )}
                />
                Completed tasks
                <span className="ml-auto font-normal tabular-nums">
                  {completedTasks.length}
                </span>
              </button>
              {completedOpen ? (
                <div className="mt-1 flex flex-col gap-0.5">
                  {completedTasks.map((task) => (
                    <SidebarTask
                      key={task.id}
                      projectId={projectId}
                      task={task}
                      active={section === "workshop" && taskId === task.id}
                      onSelect={() => setSidebarOpen(false)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </nav>
        <div className="border-t px-4 py-3">
          <NavUser showName />
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-card px-5 text-xs md:px-8">
          <Button
            variant="ghost"
            size="icon-sm"
            className="mr-1 md:hidden"
            aria-label="Open navigation"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu />
          </Button>
          <Link
            to="/p/$projectId"
            params={{ projectId }}
            className="hidden max-w-44 truncate text-muted-foreground hover:text-foreground sm:block"
          >
            {projectName}
          </Link>
          <ChevronRight className="hidden size-3 shrink-0 text-muted-foreground sm:block" />
          {section === "explorer" || section === "jig" ? (
            <>
              <ProjectBranchSelect
                onSelectBranch={(branch) => {
                  if (section === "jig") {
                    invariant(
                      scope && appId,
                      "Jig navigation requires its App identity",
                    );
                    void navigate({
                      to: "/p/$projectId/$branch/$scope/$appId",
                      params: { projectId, branch, scope, appId },
                    });
                    return;
                  }
                  void navigate({
                    to: "/p/$projectId/$branch/explorer",
                    params: { projectId, branch },
                    search: {},
                  });
                }}
                taskId={taskId}
                taskTitle={shownTaskTitle}
                tasks={activeTasks}
              />
              <ChevronRight className="size-3 shrink-0 text-muted-foreground" />
              <span className="truncate font-medium">
                {section === "jig" ? jigName : "Explorer"}
              </span>
              {taskId ? (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button asChild variant="ghost" size="icon-sm">
                        <Link
                          to="/p/$projectId/$branch/workbench/$view"
                          params={{
                            projectId,
                            branch: taskId,
                            view: "changes",
                          }}
                          search={{}}
                          aria-label="Return to task"
                        >
                          <ArrowUpRight />
                        </Link>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      Return to task
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : null}
            </>
          ) : shownTaskTitle ? (
            <>
              <Link
                to="/p/$projectId/workshop"
                params={{ projectId }}
                search={{}}
                className="text-muted-foreground hover:text-foreground"
              >
                Workshop
              </Link>
              <ChevronRight className="size-3 shrink-0 text-muted-foreground" />
              <span className="truncate font-medium">{shownTaskTitle}</span>
            </>
          ) : (
            <span className="font-medium">Workshop</span>
          )}
          <button
            type="button"
            onClick={() => setCommandOpen(true)}
            className="ml-auto flex h-9 min-w-0 items-center gap-3 rounded-md border bg-card px-3 text-left text-xs text-muted-foreground hover:text-foreground md:w-[220px] xl:w-[340px]"
            aria-label="Open command launcher"
          >
            <Search className="size-4 shrink-0 text-primary" />
            <span className="hidden flex-1 truncate md:block">
              Jump to anything...
            </span>
            <span
              className="hidden items-center gap-1 xl:flex"
              aria-hidden="true"
            >
              <kbd className="grid size-5 place-items-center rounded bg-muted text-[11px]">
                ⌘
              </kbd>
              <kbd className="grid size-5 place-items-center rounded bg-muted text-[11px]">
                K
              </kbd>
            </span>
          </button>
          {canStartAgent ? (
            <Button
              type="button"
              size="sm"
              className="h-9"
              onClick={startAgent}
              title="Start an agent here, with what you have open"
            >
              <MessageSquarePlus data-icon="inline-start" />
              <span className="hidden sm:inline">Start agent</span>
            </Button>
          ) : null}
        </header>
        <ProjectCommandContext.Provider value={registerCommands}>
          <ProjectTasksContext.Provider value={setScreenTasks}>
            <ProjectJigNameContext.Provider value={setJigName}>
              <ProjectChatsContext.Provider value={chats}>
                {children}
              </ProjectChatsContext.Provider>
            </ProjectJigNameContext.Provider>
          </ProjectTasksContext.Provider>
        </ProjectCommandContext.Provider>
      </div>
      <CommandDialog open={commandOpen} onOpenChange={setCommandOpen}>
        <ProjectCommandMenu
          groups={commandGroups}
          placeholder="Jump to anything..."
          onItemSelected={() => setCommandOpen(false)}
          listClassName="max-h-[360px]"
        />
      </CommandDialog>
    </div>
  );
}

function ProjectBranchSelect({
  onSelectBranch,
  taskId,
  taskTitle,
  tasks,
}: {
  onSelectBranch: (branch: string) => void;
  taskId?: string;
  taskTitle?: string;
  tasks: ShellTask[];
}) {
  const selectedTask = tasks.find((task) => task.id === taskId);
  const branchName = taskId
    ? (selectedTask?.title ?? taskTitle ?? taskId)
    : "main";
  const branchTasks =
    taskId && !selectedTask
      ? [...tasks, { id: taskId, title: branchName, status: "open" as const }]
      : tasks;
  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex min-w-0 shrink-0 items-center gap-0.5">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Branch: ${branchName}`}
              className="-ml-1 h-8 max-w-28 gap-1.5 px-1.5 text-xs md:max-w-36 xl:max-w-56"
            >
              <GitBranch className="size-3.5 shrink-0 text-primary" />
              <span className="truncate">{branchName}</span>
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-w-72">
            <DropdownMenuRadioGroup
              value={taskId ?? "main"}
              onValueChange={onSelectBranch}
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Shared project
              </DropdownMenuLabel>
              <DropdownMenuRadioItem value="main">main</DropdownMenuRadioItem>
              {branchTasks.length > 0 ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    Agent branches
                  </DropdownMenuLabel>
                  {branchTasks.map((task) => (
                    <DropdownMenuRadioItem key={task.id} value={task.id}>
                      <span className="truncate">{task.title}</span>
                    </DropdownMenuRadioItem>
                  ))}
                </>
              ) : null}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="About branches"
              className="text-muted-foreground"
            >
              <Info />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-64">
            Main is the shared project. A task works on its own branch until you
            review and apply its changes.
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

function SidebarTask({
  projectId,
  task,
  active,
  onSelect,
}: {
  projectId: string;
  task: ShellTask;
  active: boolean;
  onSelect: () => void;
}) {
  const liveActivity = useTaskActivity(
    projectId,
    task.status === "open" && task.activity === undefined
      ? (task.chatId ?? undefined)
      : undefined,
  );
  const state = workshopTaskState(task, task.activity ?? liveActivity);
  return (
    <Link
      to="/p/$projectId/$branch/workbench/$view"
      params={{ projectId, branch: task.id, view: "changes" }}
      search={{}}
      onClick={onSelect}
      className={cn(
        "flex items-start gap-2 rounded-md px-2.5 py-2 leading-5 text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground",
        active && "bg-sidebar-accent text-sidebar-foreground",
      )}
    >
      {state.section === "working" ? (
        <Loader2
          className="mt-1 size-3 shrink-0 animate-spin [animation-duration:3s] motion-reduce:animate-none"
          aria-hidden
        />
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            "mt-[7px] size-1.5 shrink-0 rounded-full",
            state.section === "completed"
              ? "bg-muted-foreground/50"
              : "bg-primary",
          )}
        />
      )}
      <span className="min-w-0">
        <span className="line-clamp-2 break-words">{task.title}</span>
        {task.status === "applied" && state.section !== "completed" && (
          <span className="block text-[10px] text-muted-foreground">
            {state.label}
          </span>
        )}
      </span>
    </Link>
  );
}
